import React from 'react';

import type { MediaPlayerInstance } from '@vidstack/react';

import {
    EventTypes,
    type GetCurrentTimeEvent,
    type GetDurationEvent,
    type OnPlayerEvent,
    type PauseEvent,
    type PlayEvent,
    type SetCurrentTimeEvent,
    type SnapshotEvent,
} from './types';

export type UsePlayerEventsParams = {
    /** Callback called when there is a chapter change in the video */
    onChapterChange?: (start: number) => void;
    /** Callback called when the player is unmounted */
    onDispose?: () => void;
    /** Callback called when the video ends */
    onEnded?: () => void;
    /** Callback called when you want to get the current time of the video */
    onGetCurrentTime?: (time: number) => void;
    /** Callback called when you want to get the duration of the video */
    onGetDuration?: (duration: number) => void;
    /** Callback called when metadata of the video have been loaded (native 'loadedmetadata' video event) */
    onLoadedMetadata?: () => void;
    /** Callback called when the player gets paused */
    onPause?: () => void;
    /** Callback called when the player gets play */
    onPlay?: () => void;
    /** Callback called when the player is ready */
    onReady?: () => void;
    /** Callback called after the player seeked in the video (after navigating in the video timeline) */
    onSeeked?: () => void;
    /** Callback called when a snapshot of the video has been made */
    onSnapshot?: (snapshot: Blob) => void;
};

interface UsePlayerEventsProps {
    /** Use the new local player for event handling, this ref will be used to dispatch events instead of playerRef */
    localPlayerRef?: React.RefObject<MediaPlayerInstance | null>;
    /** Ref of the iframe player */
    playerRef: React.RefObject<HTMLIFrameElement>;
    /** List of event callbacks */
    eventsCallback: UsePlayerEventsParams;
}

/**
 * Use this hook to control the player programmatically.
 * @example
 * const { setCurrentTime } = usePlayerEvents(ref);
 * setCurrentTime(20) // Go to 20s in video
 *
 * @example
 * const onChapterChange = console.log('chapter change')
 * usePlayerEvents(ref, { onChapterChange }) // logs 'chapter change' when the video goes to another chapter
 */
export const usePlayerEvents = ({
    localPlayerRef,
    playerRef,
    eventsCallback: {
        onChapterChange,
        onDispose,
        onEnded,
        onGetCurrentTime,
        onGetDuration,
        onLoadedMetadata,
        onPause,
        onPlay,
        onReady,
        onSeeked,
        onSnapshot,
    },
}: UsePlayerEventsProps) => {
    const handleMessage = React.useCallback(
        ({ data: event, source }: MessageEvent<OnPlayerEvent>) => {
            if (source !== playerRef.current?.contentWindow) {
                return;
            }
            switch (event.type) {
                case EventTypes.ChaptersChange: {
                    if (onChapterChange) {
                        onChapterChange(event.data.newChapter.startTime);
                    }

                    break;
                }

                case EventTypes.Dispose: {
                    if (onDispose) {
                        onDispose();
                    }

                    break;
                }

                case EventTypes.Ended: {
                    if (onEnded) {
                        onEnded();
                    }

                    break;
                }

                case EventTypes.GetCurrentTime: {
                    if (onGetCurrentTime) {
                        onGetCurrentTime(event.data.currentTime);
                    }

                    break;
                }

                case EventTypes.GetDuration: {
                    if (onGetDuration) {
                        onGetDuration(event.data.duration);
                    }

                    break;
                }

                case EventTypes.GetSnapshot: {
                    if (onSnapshot) {
                        onSnapshot(event.data.snapshot);
                    }

                    break;
                }

                case EventTypes.LoadedMetadata: {
                    if (onLoadedMetadata) {
                        onLoadedMetadata();
                    }

                    break;
                }

                case EventTypes.Pause: {
                    if (onPause) {
                        onPause();
                    }

                    break;
                }

                case EventTypes.Play: {
                    if (onPlay) {
                        onPlay();
                    }

                    break;
                }

                case EventTypes.Ready: {
                    if (onReady) {
                        onReady();
                    }

                    break;
                }

                case EventTypes.Seeked: {
                    if (onSeeked) {
                        onSeeked();
                    }

                    break;
                }

                default: {
                    break;
                }
            }
        },
        [
            onChapterChange,
            onDispose,
            onEnded,
            onGetCurrentTime,
            onGetDuration,
            onLoadedMetadata,
            onPause,
            onPlay,
            onReady,
            onSeeked,
            onSnapshot,
            playerRef,
        ],
    );

    const getCurrentTime = React.useCallback((): number | undefined => {
        if (localPlayerRef?.current) {
            const { currentTime } = localPlayerRef.current;
            if (onGetCurrentTime) {
                onGetCurrentTime(currentTime);
            }
            return currentTime;
        }

        const messageEvent: GetCurrentTimeEvent = {
            type: EventTypes.GetCurrentTime,
        };

        if (playerRef.current?.contentWindow) {
            playerRef.current.contentWindow.postMessage(messageEvent, '*');
        }

        return undefined;
    }, [localPlayerRef, playerRef, onGetCurrentTime]);

    const getDuration = React.useCallback(() => {
        if (localPlayerRef?.current) {
            const { duration } = localPlayerRef.current;
            if (onGetDuration) {
                onGetDuration(duration);
            }
            return;
        }

        const messageEvent: GetDurationEvent = {
            type: EventTypes.GetDuration,
        };

        if (playerRef.current?.contentWindow) {
            playerRef.current.contentWindow.postMessage(messageEvent, '*');
        }
    }, [localPlayerRef, playerRef, onGetDuration]);

    const getSnapshot = React.useCallback(() => {
        if (localPlayerRef?.current) {
            const canvas = document.createElement('canvas');
            const video = localPlayerRef.current.el?.querySelector('video');

            if (video) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    canvas.toBlob((blob) => {
                        if (blob && onSnapshot) {
                            onSnapshot(blob);
                        }
                    });
                }
            }
            return;
        }

        const messageEvent: SnapshotEvent = { type: EventTypes.GetSnapshot };

        if (playerRef.current?.contentWindow) {
            playerRef.current.contentWindow.postMessage(messageEvent, '*');
        }
    }, [localPlayerRef, playerRef, onSnapshot]);

    const pause = React.useCallback(() => {
        if (localPlayerRef?.current) {
            localPlayerRef.current.pause();
            return;
        }

        const messageEvent: PauseEvent = {
            type: EventTypes.Pause,
        };

        if (playerRef.current?.contentWindow) {
            playerRef.current.contentWindow.postMessage(messageEvent, '*');
        }
    }, [localPlayerRef, playerRef]);

    const play = React.useCallback(() => {
        if (localPlayerRef?.current) {
            localPlayerRef.current.play();
            return;
        }

        const messageEvent: PlayEvent = {
            type: EventTypes.Play,
        };

        if (playerRef.current?.contentWindow) {
            playerRef.current.contentWindow.postMessage(messageEvent, '*');
        }
    }, [localPlayerRef, playerRef]);

    const setCurrentTime = React.useCallback(
        (time: number) => {
            if (localPlayerRef?.current) {
                // eslint-disable-next-line no-param-reassign
                localPlayerRef.current.currentTime = time;
                return;
            }

            const messageEvent: SetCurrentTimeEvent = {
                type: EventTypes.SetCurrentTime,
                data: {
                    currentTime: time,
                },
            };

            if (playerRef.current?.contentWindow) {
                playerRef.current.contentWindow.postMessage(messageEvent, '*');
            }
        },
        [localPlayerRef, playerRef],
    );

    React.useEffect(() => {
        if (localPlayerRef?.current) {
            const player = localPlayerRef.current;

            const events = [
                { name: 'play' as const, handler: onPlay },
                { name: 'pause' as const, handler: onPause },
                { name: 'ended' as const, handler: onEnded },
                { name: 'seeked' as const, handler: onSeeked },
                { name: 'loaded-metadata' as const, handler: onLoadedMetadata },
                { name: 'can-play' as const, handler: onReady },
                { name: 'destroy' as const, handler: onDispose },
            ];

            events.forEach(({ name, handler }) => {
                if (handler) {
                    player.addEventListener(name, handler);
                }
            });

            return () => {
                events.forEach(({ name, handler }) => {
                    if (handler) {
                        player.removeEventListener(name, handler);
                    }
                });
            };
        }

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage, localPlayerRef, onPlay, onPause, onEnded, onSeeked, onLoadedMetadata, onReady, onDispose]);

    return {
        /** Function you can use to get the current time of the player you are referring to */
        getCurrentTime,
        /** Function you can use to get the total video duration of the player you are referring to */
        getDuration,
        /** Function you can use to take an image snapshot of the player you are referring to */
        getSnapshot,
        /** Function you can use to pause the player you are referring to */
        pause,
        /** Function you can use to play the player you are referring to */
        play,
        /** Function you can use to set the current time of the player you are referring to (to seek in the video) */
        setCurrentTime,
    };
};
